/**
 * useFavorites Hook
 *
 * Custom React hook for managing user's favorite menu items with:
 * - localStorage persistence per user
 * - Optimistic UI updates with error rollback
 * - Maximum favorites limit (100)
 * - Toast notifications for user feedback
 * - Guest mode support (no userId = empty favorites)
 *
 * @example
 * const { favorites, addFavorite, removeFavorite, isFavorite } = useFavorites();
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from './useAuth';
import { toast } from 'sonner';
import type { FavoriteMenuItem } from '@/types/favorites';
import {
  loadFavoritesFromStorage,
  saveFavoritesToStorage,
  canAddMoreFavorites,
  MAX_FAVORITES,
} from '@/lib/favorites-utils';

/**
 * Return type for useFavorites hook
 */
export interface UseFavoritesReturn {
  /** Array of favorited menu items sorted by timestamp (most recent first) */
  favorites: FavoriteMenuItem[];
  /** Loading state during initial load */
  isLoading: boolean;
  /** Error message if operation failed */
  error: string | null;
  /** Add a new favorite item */
  addFavorite: (item: FavoriteMenuItem) => void;
  /** Remove a favorite item by id */
  removeFavorite: (id: string) => void;
  /** Toggle favorite status of an item */
  toggleFavorite: (item: FavoriteMenuItem) => void;
  /** Check if an item is favorited */
  isFavorite: (id: string) => boolean;
  /** Get favorites filtered by category */
  getFavoritesByCategory: (category: string) => FavoriteMenuItem[];
  /** Clear all favorites with confirmation */
  clearAll: () => void;
}

/**
 * Custom hook for managing favorite menu items
 *
 * Provides complete CRUD operations for favorites with localStorage persistence,
 * optimistic updates, error handling, and user feedback via toast notifications.
 *
 * @returns Object with favorites state and management functions
 */
export function useFavorites(): UseFavoritesReturn {
  // Get current user ID from auth context
  const { user } = useAuth();
  const userId = user?.id;

  // State management
  const [favorites, setFavorites] = useState<FavoriteMenuItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Load favorites from localStorage on mount or when userId changes
   */
  useEffect(() => {
    if (!userId) {
      // Guest mode: no user logged in
      setFavorites([]);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const loadedFavorites = loadFavoritesFromStorage(userId);
      setFavorites(loadedFavorites);
    } catch (err) {
      const errorMessage = 'Erro ao carregar favoritos';
      console.error(errorMessage, err);
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  /**
   * Add a new favorite item
   *
   * Checks MAX_FAVORITES limit, performs optimistic update,
   * and shows appropriate toast notifications.
   */
  const addFavorite = useCallback(
    (item: FavoriteMenuItem) => {
      if (!userId) {
        toast.error('Você precisa estar logado para adicionar favoritos');
        return;
      }

      // Check if already favorited
      if (favorites.some((f) => f.id === item.id)) {
        toast.info('Este item já está nos seus favoritos');
        return;
      }

      // Check favorites limit
      if (!canAddMoreFavorites(favorites.length)) {
        toast.error(
          `Limite de favoritos atingido (${MAX_FAVORITES}). Remova alguns para adicionar mais.`
        );
        return;
      }

      try {
        // Create new item with current timestamp
        const newItem: FavoriteMenuItem = {
          ...item,
          timestamp: Date.now(),
        };

        // Optimistic update
        const updatedFavorites = [newItem, ...favorites];
        setFavorites(updatedFavorites);
        setError(null);

        // Persist to localStorage
        const saved = saveFavoritesToStorage(userId, updatedFavorites);

        if (saved) {
          toast.success(`"${item.label}" adicionado aos favoritos`);
        } else {
          // Rollback on save failure
          setFavorites(favorites);
          toast.error('Erro ao salvar favorito');
        }
      } catch (err) {
        console.error('Error adding favorite:', err);
        // Rollback on error
        setFavorites(favorites);
        toast.error('Erro ao adicionar favorito');
      }
    },
    [userId, favorites]
  );

  /**
   * Remove a favorite item by id
   *
   * Performs optimistic update with rollback on error.
   */
  const removeFavorite = useCallback(
    (id: string) => {
      if (!userId) {
        toast.error('Você precisa estar logado para remover favoritos');
        return;
      }

      try {
        // Find item for toast notification
        const item = favorites.find((f) => f.id === id);

        // Optimistic update
        const updatedFavorites = favorites.filter((f) => f.id !== id);
        setFavorites(updatedFavorites);
        setError(null);

        // Persist to localStorage
        const saved = saveFavoritesToStorage(userId, updatedFavorites);

        if (saved) {
          toast.success(
            item ? `"${item.label}" removido dos favoritos` : 'Favorito removido'
          );
        } else {
          // Rollback on save failure
          setFavorites(favorites);
          toast.error('Erro ao remover favorito');
        }
      } catch (err) {
        console.error('Error removing favorite:', err);
        // Rollback on error
        setFavorites(favorites);
        toast.error('Erro ao remover favorito');
      }
    },
    [userId, favorites]
  );

  /**
   * Toggle favorite status of an item
   *
   * Adds if not favorited, removes if already favorited.
   */
  const toggleFavorite = useCallback(
    (item: FavoriteMenuItem) => {
      const isCurrentlyFavorited = favorites.some((f) => f.id === item.id);

      if (isCurrentlyFavorited) {
        removeFavorite(item.id);
      } else {
        addFavorite(item);
      }
    },
    [favorites, addFavorite, removeFavorite]
  );

  /**
   * Check if an item is currently favorited
   */
  const isFavorite = useCallback(
    (id: string): boolean => {
      return favorites.some((f) => f.id === id);
    },
    [favorites]
  );

  /**
   * Get favorites filtered by category
   *
   * Memoized to prevent unnecessary recalculations.
   */
  const getFavoritesByCategory = useCallback(
    (category: string): FavoriteMenuItem[] => {
      return favorites.filter((f) => f.category === category);
    },
    [favorites]
  );

  /**
   * Clear all favorites with user confirmation
   *
   * Shows confirmation dialog before clearing.
   */
  const clearAll = useCallback(() => {
    if (!userId) {
      toast.error('Você precisa estar logado para limpar favoritos');
      return;
    }

    if (favorites.length === 0) {
      toast.info('Você não tem favoritos para remover');
      return;
    }

    // Show confirmation dialog
    const confirmed = window.confirm(
      `Tem certeza que deseja remover todos os ${favorites.length} favoritos?`
    );

    if (!confirmed) {
      return;
    }

    try {
      // Store previous favorites for rollback
      const previousFavorites = [...favorites];

      // Optimistic update
      setFavorites([]);
      setError(null);

      // Persist to localStorage
      const saved = saveFavoritesToStorage(userId, []);

      if (saved) {
        toast.success('Todos os favoritos foram removidos');
      } else {
        // Rollback on save failure
        setFavorites(previousFavorites);
        toast.error('Erro ao limpar favoritos');
      }
    } catch (err) {
      console.error('Error clearing favorites:', err);
      toast.error('Erro ao limpar favoritos');
    }
  }, [userId, favorites]);

  return {
    favorites,
    isLoading,
    error,
    addFavorite,
    removeFavorite,
    toggleFavorite,
    isFavorite,
    getFavoritesByCategory,
    clearAll,
  };
}
